import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { blogPosts } from '@/data/blogPosts'
import { caseStudies } from '@/data/caseStudies'
import { solutions } from '@/data/solutions'
import { functions } from '@/data/functions'
import { comparisons } from '@/data/comparisons'
import { glossaryEntries } from '@/data/glossary'

// Internal route used only by the prerender step (scripts/prerender.mjs).
// Renders a 1200x630 social-card image for blog posts, case studies, and
// solution pages. Puppeteer navigates here, waits for the `data-og-ready`
// marker, and screenshots to dist/og/<type>/<slug>.png. The detail pages
// then reference that PNG via the useSEO `ogImage` prop, so link previews
// on Twitter/LinkedIn/Slack show a card with the page-specific title and
// category instead of the generic Algoscale logo.

interface CardContent {
  kicker: string
  title: string
  meta?: string
}

function resolveContent(type: string, slug: string): CardContent | null {
  if (type === 'blog') {
    const post = blogPosts.find((p) => p.slug === slug)
    if (!post) return null
    return { kicker: post.category, title: post.title, meta: post.readTime }
  }
  if (type === 'case-study') {
    const study = caseStudies.find((c) => c.slug === slug)
    if (!study) return null
    return { kicker: study.industry, title: study.headline, meta: study.companyType }
  }
  if (type === 'industry') {
    const sol = solutions.find((s) => s.slug === slug)
    if (!sol) return null
    return { kicker: sol.industry, title: sol.headline || `${sol.industry} document extraction`, meta: 'Industry solution' }
  }
  if (type === 'function') {
    const fn = functions.find((f) => f.slug === slug)
    if (!fn) return null
    return { kicker: fn.name, title: fn.headline, meta: 'Function solution' }
  }
  if (type === 'compare') {
    const comp = comparisons.find((c) => c.slug === slug)
    if (!comp) return null
    return { kicker: `DocumentIQ vs ${comp.competitor}`, title: comp.headline, meta: undefined }
  }
  if (type === 'glossary') {
    const entry = glossaryEntries.find((e) => e.slug === slug)
    if (!entry) return null
    return { kicker: 'Glossary', title: entry.term, meta: entry.shortDefinition }
  }
  if (type === 'home') {
    return {
      kicker: 'DocumentIQ by Algoscale',
      title: 'AI-Powered Document Data Extraction',
      meta: 'Extract structured data from PDFs at scale',
    }
  }
  return null
}

export default function OGImage() {
  const { type, slug } = useParams<{ type: string; slug: string }>()
  const content = type && slug ? resolveContent(type, slug) : null

  useEffect(() => {
    document.documentElement.setAttribute('data-og-ready', 'true')
    document.body.style.margin = '0'
    document.body.style.background = '#0f172a'
    return () => {
      document.documentElement.removeAttribute('data-og-ready')
      document.body.style.margin = ''
      document.body.style.background = ''
    }
  }, [type, slug, content])

  if (!content) {
    return (
      <div style={{ width: 1200, height: 630, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: 'white', fontFamily: 'system-ui, sans-serif' }}>
        <p>Not found: {type}/{slug}</p>
      </div>
    )
  }

  return (
    <div
      style={{
        width: 1200,
        height: 630,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
        color: 'white',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.35) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(99,102,241,0.3) 0%, transparent 55%)',
        }}
      />
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '64px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img
            src="/algoscale-logo.png"
            alt=""
            width={172}
            height={49}
            style={{ height: 40, width: 'auto', filter: 'brightness(0) invert(1)' }}
          />
          <span style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em' }}>DocumentIQ</span>
        </div>

        <div>
          <div
            style={{
              display: 'inline-block',
              padding: '8px 20px',
              borderRadius: 9999,
              background: 'rgba(59,130,246,0.25)',
              border: '1px solid rgba(147,197,253,0.4)',
              color: '#bfdbfe',
              fontSize: 18,
              fontWeight: 500,
              marginBottom: 28,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            {content.kicker}
          </div>
          <h1
            style={{
              fontSize: content.title.length > 80 ? 52 : 64,
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              margin: 0,
              maxWidth: 1040,
            }}
          >
            {content.title}
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ color: '#cbd5f5', fontSize: 22 }}>documentiq.algoscale.com</div>
          {content.meta && (
            <div
              style={{
                color: '#cbd5f5',
                fontSize: 20,
                background: 'rgba(15,23,42,0.4)',
                padding: '8px 18px',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {content.meta}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
